import type { Scenario, ScenarioContent, DidAgentBlock } from "@/lib/powtoon-be/types";
import type { ScenarioAdapter, ScenarioView, ScenarioFormState } from "../types";
import { getMode } from "@/lib/scenarios/interactionModes";
import { composeBlockPrompt } from "../composePrompt";
import { strippedForeignFields } from "../spec";
import { didBackgroundPreview } from "./backgrounds";

export const didAdapter: ScenarioAdapter = {
  toView(scenario: Scenario): ScenarioView {
    const c = scenario.content ?? {};
    const block = c.did;
    return {
      provider: "did",
      name: scenario.name,
      description: scenario.description,
      thumbnailUrl: scenario.thumb_url || undefined,
      systemPrompt: composeBlockPrompt(block),
      openingText: block?.opening_text,
      faceId: block?.presenter_id,
      voiceId: block?.voice_id,
      language: c.language,
      inputs: block?.inputs ?? [],
    };
  },
  deriveThumbnail(scenario) {
    return scenario.thumb_url || undefined;
  },
  deriveBackground(scenario) {
    const block = scenario.content?.did;
    return didBackgroundPreview(block?.background_id, block?.background_url);
  },
  toFormState(scenario: Scenario): Partial<ScenarioFormState> {
    const c = scenario.content ?? {};
    const block = c.did;
    return {
      avatarId: block?.presenter_id ?? "",
      voiceId: block?.voice_id ?? "",
      language: c.language ?? "",
      persona: block?.persona ?? "",
      backgroundId: block?.background_id ?? "none",
      backgroundUrl: block?.background_url ?? "",
      knowledge: block?.knowledge ?? block?.system_prompt ?? "",
      instructions: block?.instructions ?? "",
      openingText: block?.opening_text ?? "",
      mode: block?.interaction_mode ?? "conversational",
      didKnowledgeId: block?.knowledge_id,
      didKnowledgeDocuments: block?.knowledge_documents ?? [],
      didKnowledgeGrounded: block?.knowledge_grounded ?? false,
      didPresentDocument: block?.present_document ?? false,
      didPresentationToolId: block?.presentation_tool_id,
      didCloseToolId: block?.close_tool_id,
      didPresentationToolIds:
        block?.presentation_tool_ids ??
        [block?.presentation_tool_id, block?.close_tool_id].filter((v): v is string => !!v),
    };
  },
  collectSaveMeta(scenario) {
    const block = scenario?.content?.did;
    return {
      initialAgentId: block?.presenter_type === "expressive" ? block?.agent_id : undefined,
      initialVoiceId: block?.voice_id,
      initialQuestionnaireIds: block?.questionnaire_ids ?? [],
      initialRoleplayIds: block?.roleplay_ids ?? [],
    };
  },
  toContent(state: ScenarioFormState): ScenarioContent {
    const prev = state.previous ?? {};
    const prevBlock = prev.did;
    const previousExpressiveAgentId =
      prevBlock?.presenter_type === "expressive" ? prevBlock.agent_id : undefined;
    const modeMeta = getMode(state.mode);
    const nextBlock: DidAgentBlock = {
      // Preserve only Expressive agents. Older clip agents cannot publish mic
      // audio, so re-saving must create a fresh voice-capable D-ID agent.
      agent_id: previousExpressiveAgentId,
      presenter_id: state.avatarId || undefined,
      presenter_type: "expressive",
      voice_id: state.voiceId || undefined,
      // Background selection is editor-owned; the edge function turns the id
      // into the absolute image URL D-ID stores on the presenter.
      background_id: state.backgroundId || "none",
      background_url: state.backgroundUrl || undefined,
      // The provider is resolved from the live catalog at sync time and read
      // back from D-ID, so carry the previous value only as a placeholder.
      voice_provider: prevBlock?.voice_provider,
      system_prompt: state.generatedPrompt,
      persona: modeMeta.showPersona ? state.persona : "",
      knowledge: state.knowledge,
      instructions: modeMeta.showInstructions ? state.instructions : "",
      opening_text: state.openingText,
      // Mode + questionnaire versioning live on the block for D-ID (no
      // context block). `questionnaire_ids` is refreshed by `saveScenario`.
      interaction_mode: state.mode,
      questionnaire_ids: prevBlock?.questionnaire_ids,
      roleplay_ids: prevBlock?.roleplay_ids,
      roleplay_scenario_id:
        state.mode === "role_play" ? prevBlock?.roleplay_scenario_id : undefined,
      // Knowledge base ownership is editor-driven: documents are registered
      // before save, `saveScenario` only reconciles and attaches them.
      knowledge_id: state.didKnowledgeId || undefined,
      knowledge_documents: state.didKnowledgeDocuments ?? [],
      knowledge_grounded: !!state.didKnowledgeGrounded,
      // Conversational-only capability. The display source is only resolved
      // after uploads, so saveScenario applies that half of the gate (and
      // reconciles the tool itself).
      present_document: state.mode === "conversational" && !!state.didPresentDocument,
      presentation_tool_id: state.didPresentationToolId,
      close_tool_id: state.didCloseToolId,
      presentation_tool_ids: state.didPresentationToolIds?.length
        ? state.didPresentationToolIds
        : undefined,
      // Placeholder — saveScenario overwrites with post-upload finals.
      inputs: state.initialInputs,
    };
    return {
      ...prev,
      provider: "did",
      // Clears every block and top-level field owned by the other providers,
      // derived from the provider spec registry.
      ...strippedForeignFields("did"),
      // Language is a D-ID conversation parameter — persist at the top
      // level so the session frame can forward it at start time.
      language: state.language || undefined,
      publish_mode: state.publishMode,
      auto_start: state.autoStart,
      cta_type: state.ctaType,
      cta_url: state.ctaType === "sales" ? state.ctaUrl.trim() : "",
      redirect_url: state.redirectUrl.trim(),
      display: state.display,
      did: nextBlock,
    };
  },
};